import { useEffect, useRef, useState } from 'react';
import { voiceTime } from './consoleVoice';
import type { ConsoleVoiceAccess, ConsoleVoiceMetadata } from './huddleApi';
import './consoleVoice.css';
export function ConsoleVoiceMessage({ id, access }: { id: string; access: ConsoleVoiceAccess }) {
  const [metadata, setMetadata] = useState<ConsoleVoiceMetadata | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [position, setPosition] = useState(0);
  const [audible, setAudible] = useState(false);
  const api = useRef(access);
  api.current = access;
  const audio = useRef<HTMLAudioElement>(null);
  const signed = useRef<{ url: string; expires: number } | null>(null);
  const epoch = useRef(0);
  const playing = useRef(false);
  useEffect(() => {
    const version = ++epoch.current;
    void api.current
      .metadata(id)
      .then((value) => {
        if (version === epoch.current) setMetadata(value);
      })
      .catch(() => {
        if (version === epoch.current) {
          setMetadata(null);
          setError('Voice note unavailable');
        }
      });
    const player = audio.current;
    return () => {
      ++epoch.current;
      player?.pause();
      player?.removeAttribute('src');
      signed.current = null;
    };
  }, [id]);
  async function play() {
    if (playing.current) return;
    const player = audio.current;
    if (!player) return;
    if (!player.paused) {
      player.pause();
      return;
    }
    playing.current = true;
    setBusy(true);
    setError('');
    const version = epoch.current;
    try {
      // Reauthorize every Play. Never sign during metadata load or polling.
      const result = await api.current.play(id);
      if (version !== epoch.current) return;
      const position = player.currentTime;
      if (signed.current?.url !== result.url) {
        player.src = result.url;
        player.currentTime = position;
      }
      signed.current = { url: result.url, expires: Date.now() + 300000 };
      await player.play();
    } catch {
      if (version === epoch.current) {
        player.pause();
        player.removeAttribute('src');
        signed.current = null;
        try {
          const refreshed = await api.current.metadata(id);
          if (version === epoch.current) setMetadata(refreshed);
        } catch {
          if (version === epoch.current) setMetadata(null);
        }
        if (version === epoch.current)
          setError('Audio no longer available. Reopen the chat to check access.');
      }
    } finally {
      playing.current = false;
      if (version === epoch.current) setBusy(false);
    }
  }
  const [showTranscript, setShowTranscript] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const hasTranscript = Boolean(metadata?.transcript.trim());
  const transcriptLabel = `transcript${metadata?.partial ? ' (partial)' : ''}`;
  return (
    <div className="console-voice-message">
      <div className="console-voice-row">
        {metadata?.audio_available ? (
          <button
            className="console-voice-play"
            type="button"
            aria-label="Play / pause voice note"
            disabled={busy}
            onClick={() => void play()}
          >
            {busy ? (
              <span aria-hidden="true">…</span>
            ) : (
              <svg
                data-icon={audible ? 'pause' : 'play'}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                {audible ? (
                  <path fill="currentColor" d="M6 5h4v14H6zM14 5h4v14h-4z" />
                ) : (
                  <path fill="currentColor" d="M8 5v14l11-7z" />
                )}
              </svg>
            )}
          </button>
        ) : (
          <p>{error || 'Audio no longer available'}</p>
        )}
        <audio
          ref={audio}
          preload="none"
          onPlay={() => setAudible(true)}
          onPause={() => setAudible(false)}
          onTimeUpdate={() => setPosition(audio.current?.currentTime ?? 0)}
          onEnded={() => setBusy(false)}
        />
        {metadata?.audio_available ? (
          <input
            className="console-voice-bar"
            type="range"
            aria-label="Voice note position"
            min="0"
            max={metadata.duration}
            step="0.1"
            value={position}
            onChange={(event) => {
              if (audio.current) audio.current.currentTime = Number(event.target.value);
              setPosition(Number(event.target.value));
            }}
          />
        ) : null}
        {metadata ? (
          <span className="console-voice-time">
            {voiceTime(audible || position > 0 ? position : metadata.duration)}
          </span>
        ) : null}
        {metadata ? (
          <button
            className="console-voice-more"
            type="button"
            aria-label="Voice note details"
            aria-expanded={showMore}
            onClick={() => setShowMore(!showMore)}
          >
            ⋯
          </button>
        ) : null}
      </div>
      {metadata && showMore ? (
        <small>Audio retained until {new Date(metadata.expires_at).toLocaleDateString()}</small>
      ) : null}
      {metadata && hasTranscript ? (
        <>
          <button
            className="console-voice-toggle"
            type="button"
            aria-expanded={showTranscript}
            onClick={() => setShowTranscript(!showTranscript)}
          >
            {showTranscript ? `Hide ${transcriptLabel}` : `Show ${transcriptLabel}`}
          </button>
          {showTranscript ? (
            <p className="console-voice-transcript">{metadata.transcript}</p>
          ) : null}
        </>
      ) : null}
      {error ? <p role="status">{error}</p> : null}
    </div>
  );
}
